@let current = currentField();
@let family = color();
@let isBlocked = blocked();
@let isDefault = type() === cardTypes.DEFAULT;

<card-base
    [field]="field()"
    [favoriteField]="favoriteField()"
    [trashField]="trashField()"
    [type]="type()"
    [disabled]="isBlocked"
    [selected]="selected()"
    [showAi]="false"
    [textZone]="false"
    [block]="true">

    <!-- Zona 1 do rail: "Aparência", com a cor, o estilo e as colunas do PDF no mesmo
         menu. Os indicadores não têm texto corrido: o rail fica só com esta zona e o pé. -->
    <div
        card-toolbar
        class="stats-rail">
        @if (isDefault) {
            <div class="rail-chip-wrapper">
                <button
                    type="button"
                    class="rail-chip"
                    [class.open]="look.isOpen()"
                    [class.default-disabled]="isBlocked"
                    [attr.aria-label]="'document.edit.railLook.appearance' | translate"
                    [raEditTooltip]="look.isOpen() ? null : { text: ('document.edit.railLook.appearance' | translate), position: 'left' }"
                    [raLookPanel]="lookTpl"
                    lookPanelTitle="document.edit.railLook.appearance"
                    [lookPanelItemId]="field()?._id"
                    [lookPanelDisabled]="isBlocked"
                    #look="lookPanel"
                    (click)="look.toggle()">
                    <ra-icon icon="palette" varColor="currentColor" [size]="22" />
                </button>

                <ng-template #lookTpl>
                    <div class="look-panel-body">
                        <p class="look-title">{{ 'document.edit.statsCard.color' | translate }}</p>
                        <div class="look-swatches">
                            @for (option of colorOptions; track option.key) {
                                <button
                                    type="button"
                                    class="look-swatch"
                                    [class.current]="family.key === option.key"
                                    [style.background-color]="option.accent"
                                    [attr.aria-label]="option.labelKey | translate"
                                    [attr.aria-pressed]="family.key === option.key"
                                    [raEditTooltip]="{ text: (option.labelKey | translate), position: 'top' }"
                                    (click)="selectColor(option.key)">
                                </button>
                            }
                            <custom-color-swatch
                                [value]="family.accent"
                                [current]="family.key === 'custom'"
                                [label]="'document.edit.calloutCard.colorCustom' | translate"
                                (preview)="colorPreview.set($event)"
                                (choose)="chooseCustomColor($event)" />
                        </div>

                        <span class="look-divider"></span>
                        <p class="look-title">{{ 'document.edit.statsCard.style' | translate }}</p>
                        <div class="look-grid" style="--look-cols: 2">
                            @for (option of styleOptions; track option.key) {
                                <button
                                    type="button"
                                    class="look-option"
                                    [class.current]="styleKey() === option.key"
                                    (click)="selectStyle(option.key)">
                                    <span
                                        aria-hidden="true"
                                        [class]="'style-sample style-sample-large style-sample-' + option.key"
                                        [style.--stats-number]="family.statNumber"
                                        [style.--stats-ink]="family.statInk"
                                        [style.--stats-fill]="family.fill"
                                        [style.--stats-line]="family.statLine">
                                        <i class="sample-value"></i>
                                        <i class="sample-label"></i>
                                        <i class="sample-label short"></i>
                                    </span>
                                    <span>{{ option.labelKey | translate }}</span>
                                </button>
                            }
                        </div>

                        <span class="look-divider"></span>
                        <p class="look-title">{{ 'document.edit.statsCard.columns' | translate }}</p>
                        <div class="look-grid">
                            @for (option of columnOptions; track option) {
                                <button
                                    type="button"
                                    class="look-option"
                                    [class.current]="columns() === option"
                                    (click)="selectColumns(option)">
                                    <span class="cols-glyph" aria-hidden="true">
                                        @for (slot of columnSlots(option); track slot) {
                                            <i></i>
                                        }
                                    </span>
                                    <span>{{ option }}</span>
                                </button>
                            }
                        </div>
                    </div>
                </ng-template>
            </div>
        }
    </div>

    @if (isDefault) {
        <!-- A grade como imprime, editável no lugar. -->
        <div
            cdkDropList
            cdkDropListOrientation="mixed"
            [cdkDropListData]="entries()"
            [cdkDropListDisabled]="isBlocked"
            (cdkDropListDropped)="drop($event)"
            [class]="'stats-editor stats-' + styleKey()"
            [style.--stats-cols]="columns()"
            [style.--stats-number]="family.statNumber"
            [style.--stats-ink]="family.statInk"
            [style.--stats-fill]="family.fill"
            [style.--stats-line]="family.statLine"
            (focusin)="onFocusIn()"
            (focusout)="onFocusOut($event)"
            (click)="$event.stopPropagation()">

            @for (entry of entries(); track $index; let i = $index) {
                <div class="stat" cdkDrag [cdkDragData]="entry">
                    <span
                        class="stat-grip"
                        cdkDragHandle
                        [attr.aria-label]="'document.edit.statsCard.drag' | translate"
                        [raEditTooltip]="{ text: ('document.edit.statsCard.drag' | translate), position: 'top' }">
                        <ra-icon icon="menu" varColor="currentColor" [size]="12" />
                    </span>

                    <!-- Fora do Tab (como na tabela e no semáforo): o Tab anda de campo em
                         campo, número, legenda e o número do próximo indicador. -->
                    <button
                        type="button"
                        class="stat-remove"
                        tabindex="-1"
                        [disabled]="isBlocked"
                        [attr.aria-label]="'document.edit.statsCard.remove' | translate"
                        [raEditTooltip]="{ text: ('document.edit.statsCard.remove' | translate), position: 'top' }"
                        (click)="removeEntry(i)">
                        <ra-icon icon="trash" varColor="currentColor" [size]="14" />
                    </button>

                    <input
                        type="text"
                        class="stat-value"
                        autocomplete="off"
                        [value]="entry.value"
                        [attr.maxlength]="limits.MAX_VALUE_LENGTH"
                        [disabled]="isBlocked"
                        [placeholder]="'document.edit.statsCard.valuePlaceholder' | translate"
                        (input)="onValueInput(i, $event)"
                        (change)="commit()"
                        (keydown)="blurOnEnter($event)"
                    />

                    <textarea
                        #labelArea
                        class="stat-label"
                        rows="1"
                        [value]="entry.label"
                        [attr.maxlength]="limits.MAX_LABEL_LENGTH"
                        [disabled]="isBlocked"
                        [placeholder]="'document.edit.statsCard.labelPlaceholder' | translate"
                        (input)="onLabelInput(i, $event)"
                        (change)="commit()"
                        (keydown)="blurOnEnter($event)"></textarea>
                </div>
            }

            @if (canAdd()) {
                <button type="button" class="stat-add" [disabled]="isBlocked" (click)="addEntry()">
                    <ra-icon icon="add" varColor="currentColor" [size]="16" />
                    <span>{{ 'document.edit.statsCard.add' | translate }}</span>
                </button>
            }
        </div>
    } @else {
        <stats-grid
            [value]="current.value"
            [columns]="current.configs?.statsColumns"
            [styleKey]="current.configs?.statsStyle"
            [color]="current.configs?.calloutColor" />
    }
</card-base>
